import { useNavigate } from 'react-router';
import { Link } from 'react-router';
import { SESSION_KINDS, type OpsContainer, type OpsTrip } from '@ecobox/firebase';
import { Badge, Button, EmptyState, TimeInState, useNow } from '@ecobox/ui';
import { SessionCards } from '../components/SessionCards';
import { useImportContainers, useInboundTrips } from '../hooks/useOps';
import {
  containerLabel,
  containerStatusLabel,
  containerStatusTone,
  dueClock,
  tripLabel,
  tripStatusLabel,
} from '../labels';
import { useBranch, useSession } from '../services';
import { useAllSessions, usePermissions, useSessionEngine } from '../sessions';

export function ReceivePage() {
  const branch = useBranch().current;
  const { uid } = useSession();
  const engine = useSessionEngine();
  const sessions = useAllSessions();
  const perms = usePermissions();
  const navigate = useNavigate();
  const trips = useInboundTrips(branch);
  const containers = useImportContainers(branch);
  const canReceive = perms.has('trip.unload');
  const now = useNow();

  const mine = sessions.filter(
    (s) =>
      s.uid === uid &&
      s.finishedAt === undefined &&
      s.branchId === branch &&
      SESSION_KINDS[s.kind].direction === 'receive',
  );

  const start = (kind: 'receive_trip' | 'receive_container', targetId: string, label: string) => {
    if (!uid || !branch) return;
    const session = engine.start({ kind, targetId, targetLabel: label, uid, branchId: branch });
    void navigate(`/session/${session.id}`);
  };

  if (!canReceive) {
    return (
      <EmptyState title="Receiving is not part of your role" description="Ask your manager." />
    );
  }

  const receivable = (c: OpsContainer) => c.status === 'pulled_out' || c.status === 'receiving';

  return (
    <div className="ops-stack">
      <h1 className="ops-title">Receive</h1>
      <SessionCards sessions={mine} />

      <h2 className="ops-subtitle" data-tour="inbound-trips">
        Trips coming to {branch ?? '…'}
      </h2>
      {trips.error ? (
        <p role="alert" className="ops-error">
          {trips.error}
        </p>
      ) : null}
      {trips.data.length === 0 ? (
        <p className="ops-muted">
          {trips.loading ? 'Loading…' : 'No trips are on their way here.'}
        </p>
      ) : (
        <ul className="ops-list" aria-label="Inbound trips">
          {trips.data.map((t: OpsTrip) => (
            <li key={t.id} className="ops-row">
              <div className="ops-row__top">
                <strong>{tripLabel(t)}</strong>
                <Badge tone={t.status === 'receiving' ? 'info' : 'warning'}>
                  {tripStatusLabel(t.status)}
                </Badge>
              </div>
              <div className="ops-muted">
                {t.counts.loaded} loaded · {t.counts.received} received ·{' '}
                <TimeInState
                  since={t.stateSince}
                  clock={dueClock(t.nextDueAt, now)}
                  suffix={tripStatusLabel(t.status).toLowerCase()}
                />
              </div>
              <Button
                size="touch"
                variant="primary"
                block
                onClick={() => start('receive_trip', t.id, tripLabel(t))}
              >
                Scan boxes off this trip
              </Button>
            </li>
          ))}
        </ul>
      )}

      <h2 className="ops-subtitle" data-tour="inbound-containers">
        Containers for {branch ?? '…'}
      </h2>
      {containers.data.length === 0 ? (
        <p className="ops-muted">{containers.loading ? 'Loading…' : 'No containers are coming.'}</p>
      ) : (
        <ul className="ops-list" aria-label="Inbound containers">
          {containers.data.map((c) => (
            <li key={c.id} className="ops-row">
              <div className="ops-row__top">
                <strong>{containerLabel(c)}</strong>
                <Badge tone={containerStatusTone(c.status)}>{containerStatusLabel(c.status)}</Badge>
              </div>
              <div className="ops-muted">
                {c.counts.loaded} expected · {c.counts.received} received ·{' '}
                <TimeInState
                  since={c.stateSince}
                  clock={dueClock(c.nextDueAt, now)}
                  suffix={containerStatusLabel(c.status).toLowerCase()}
                />
              </div>
              {receivable(c) ? (
                <Button
                  size="touch"
                  variant="primary"
                  block
                  onClick={() => start('receive_container', c.id, containerLabel(c))}
                >
                  Devan: scan boxes out
                </Button>
              ) : (
                <p className="ops-muted" style={{ margin: 0 }}>
                  Not at the hub yet. Record its steps under <Link to="/yard">Container yard</Link>.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
